/**
 * Bouton — la seule surface cliquable stylée de l’application.
 *
 * `classesBouton()` est exporté pour que les `<Link>` de React Router puissent
 * porter exactement la même allure sans dupliquer les classes.
 */

import { use, type ComponentPropsWithRef, type ReactNode } from 'react'
import { LIBELLES_DROITS_METIER, peutExercer, type DroitMetier } from '@tb/shared'
import { ContexteAuth } from '../auth/contexte'

import { cn } from '../lib/cn'
import { Spinner } from './Spinner'

export type VarianteBouton = 'primaire' | 'secondaire' | 'discret' | 'danger' | 'lien'
export type TailleBouton = 'sm' | 'md' | 'lg' | 'xl'

const BASE =
  'inline-flex select-none items-center justify-center gap-2 whitespace-nowrap rounded-md ' +
  'font-medium transition-colors disabled:pointer-events-none disabled:opacity-50'

const VARIANTES: Readonly<Record<VarianteBouton, string>> = {
  primaire: 'bg-rideau-700 text-white shadow-sm hover:bg-rideau-800 active:bg-rideau-900',
  secondaire:
    'bg-white text-slate-800 ring-1 ring-slate-300 ring-inset hover:bg-slate-50 active:bg-slate-100',
  discret: 'text-slate-700 hover:bg-slate-100 active:bg-slate-200',
  danger: 'bg-red-700 text-white shadow-sm hover:bg-red-800 active:bg-red-900',
  lien: 'text-rideau-700 underline underline-offset-2 hover:text-rideau-900',
}

// Tailles adaptatives : Anne consulte son back-office depuis son téléphone.
// Sous `sm` (< 640 px), **aucune** taille ne descend sous 44 px — la densité
// n’a pas de sens au doigt ; au-delà, on retrouve celle d’un écran de bureau,
// où le pointeur est précis et où la place compte.
const TAILLES: Readonly<Record<TailleBouton, string>> = {
  sm: 'h-11 px-3 text-sm sm:h-8 sm:px-2.5',
  md: 'h-11 px-4 text-sm sm:h-10',
  lg: 'h-12 px-5 text-base',
  // `xl` existe pour l’app de scan : on l’utilise avec des gants, dans le noir.
  xl: 'h-14 px-6 text-lg',
}

export function classesBouton(
  variante: VarianteBouton = 'primaire',
  taille: TailleBouton = 'md',
  pleineLargeur = false,
): string {
  return cn(
    BASE,
    VARIANTES[variante],
    // Le bouton `lien` n’a pas de hauteur propre : au doigt, on lui garantit
    // quand même une bande tapable de 44 px.
    variante === 'lien' ? 'h-auto min-h-11 px-0 sm:min-h-0' : TAILLES[taille],
    pleineLargeur && 'w-full',
  )
}

export interface ButtonProps extends ComponentPropsWithRef<'button'> {
  readonly permission?: DroitMetier | undefined
  readonly variante?: VarianteBouton | undefined
  readonly taille?: TailleBouton | undefined
  /** Affiche un spinner et neutralise le bouton le temps de la mutation. */
  readonly chargement?: boolean | undefined
  readonly pleineLargeur?: boolean | undefined
  readonly iconeAvant?: ReactNode | undefined
  readonly iconeApres?: ReactNode | undefined
}

export function Button({
  permission,
  variante = 'primaire',
  taille = 'md',
  chargement = false,
  pleineLargeur = false,
  iconeAvant,
  iconeApres,
  className,
  children,
  disabled,
  type = 'button',
  title,
  ...reste
}: ButtonProps) {
  const session = use(ContexteAuth)?.session
  const interdit =
    permission !== undefined &&
    session?.sujet === 'staff' &&
    !peutExercer(session.utilisateur, permission)
  return (
    <button
      // Un bouton dans un formulaire ne soumet que si on le demande :
      // le défaut `submit` du HTML a déjà coûté assez de validations accidentelles.
      type={type}
      className={cn(classesBouton(variante, taille, pleineLargeur), className)}
      disabled={disabled === true || chargement || interdit}
      title={
        interdit && permission ? `Droit requis : ${LIBELLES_DROITS_METIER[permission]}` : title
      }
      aria-busy={chargement || undefined}
      {...reste}
    >
      {chargement ? <Spinner taille="sm" /> : iconeAvant}
      {children}
      {iconeApres}
    </button>
  )
}
